/**
 * Work Schedule Content Component
 *
 * Weekly schedule view for operators/packers/stations.
 * Used as a tab content in the combined Schedule page.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, Copy, Users, Factory, Check, X, Printer, Download } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import { useLanguage } from '../../i18n/LanguageContext';
import { useAuth } from '../../hooks/useAuth';
import { toLocalISODate } from '../../utils/dateUtils';
import { wsService } from '../../services/websocket';
import workScheduleService, {
  WeeklyScheduleResponse,
  JobType,
  JobAssignment,
  StationSchedule,
  OperatorStationAssignment,
  Employee,
  Station,
} from '../../services/workScheduleService';
import toast from 'react-hot-toast';

// Helper: Get Monday of the week for a given date
const getMonday = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Helper: Add days to a date
const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

// Helper: Format date for display (Mon 2/10)
const formatDayHeader = (date: Date): { day: string; date: string } => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return {
    day: days[date.getDay()],
    date: `${date.getMonth() + 1}/${date.getDate()}`,
  };
};

// Job code to role mapping (for display)
const JOB_ROLE_MAP: Record<string, string> = {
  '81000': 'PREP',
  '82000': 'PROC',
  '83000': 'COOK',
  '84000': 'CUT',
  '85000': 'PACK',
  '86000': 'GENE',
};

const WorkScheduleContent: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const isReadOnly = user?.role === 'O' || user?.role === 'operator';

  // State
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => getMonday(new Date()));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scheduleData, setScheduleData] = useState<WeeklyScheduleResponse | null>(null);
  const [jobTypes, setJobTypes] = useState<JobType[]>([]);
  const latestLoadRequestRef = useRef(0);
  const isSavingRef = useRef(false);

  // Track pending changes (employee assignments, station schedules, and operator station assignments)
  const [pendingAssignments, setPendingAssignments] = useState<Map<string, string | null>>(new Map());
  const [pendingStations, setPendingStations] = useState<Map<string, boolean>>(new Map());
  const [pendingOperatorStations, setPendingOperatorStations] = useState<Map<string, number[]>>(new Map());
  const [hasChanges, setHasChanges] = useState(false);

  // Get week days (Mon-Sun)
  const weekDays = useMemo(() => {
    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(currentWeekStart, i));
    }
    return days;
  }, [currentWeekStart]);

  // Load schedule data
  const loadScheduleData = useCallback(async (options?: { preservePending?: boolean; showLoading?: boolean }) => {
    const preservePending = options?.preservePending ?? false;
    const showLoading = options?.showLoading ?? !preservePending;
    const requestId = ++latestLoadRequestRef.current;

    if (showLoading) {
      setLoading(true);
    }

    try {
      const schedule = await workScheduleService.getWeeklySchedule(toLocalISODate(currentWeekStart));

      if (requestId !== latestLoadRequestRef.current) {
        return;
      }

      setScheduleData(schedule);
      // jobTypes is included in the weekly schedule response
      setJobTypes(schedule.jobTypes || []);
      if (!preservePending) {
        setPendingAssignments(new Map());
        setPendingStations(new Map());
        setPendingOperatorStations(new Map());
        setHasChanges(false);
      }
    } catch (error) {
      if (requestId !== latestLoadRequestRef.current) {
        return;
      }

      console.error('Failed to load schedule:', error);
      toast.error(t('workSchedule.loadError'));
    } finally {
      if (showLoading && requestId === latestLoadRequestRef.current) {
        setLoading(false);
      }
    }
  }, [currentWeekStart, t]);

  useEffect(() => {
    loadScheduleData();
  }, [loadScheduleData]);

  useEffect(() => {
    wsService.connect();

    const handleScheduleUpdated = () => {
      if (isSavingRef.current) {
        return;
      }

      void loadScheduleData({ preservePending: true, showLoading: false });
    };

    wsService.on('schedule-updated', handleScheduleUpdated);

    return () => {
      wsService.off('schedule-updated', handleScheduleUpdated);
    };
  }, [loadScheduleData]);

  // Navigate weeks
  const navigateWeek = (direction: number) => {
    setCurrentWeekStart((prev) => addDays(prev, direction * 7));
  };

  const goToCurrentWeek = () => {
    setCurrentWeekStart(getMonday(new Date()));
  };

  // Get week header text
  const getWeekHeader = (): string => {
    const endDate = addDays(currentWeekStart, 6);
    const fmt = (d: Date) => `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
    return `${fmt(currentWeekStart)} - ${fmt(endDate)}`;
  };

  // Short week header for mobile: "Apr-06 - Apr-12"
  const getWeekHeaderShort = (): string => {
    const endDate = addDays(currentWeekStart, 6);
    const fmt = (d: Date) => `${d.toLocaleDateString('en-US', { month: 'short' })}/${String(d.getDate()).padStart(2, '0')}`;
    return `${fmt(currentWeekStart)} - ${fmt(endDate)}`;
  };

  // Check if a date is in the past (before today - today is editable)
  const isPastDate = (date: Date): boolean => {
    // Use local date comparison to avoid timezone issues
    const today = new Date();
    const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const dateLocal = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return dateLocal < todayLocal;
  };

  // Get assignment key
  const getAssignmentKey = (employeeNumber: string, date: string): string => {
    return `${employeeNumber}:${date}`;
  };

  // Get station key
  const getStationKey = (stationId: number, date: string): string => {
    return `${stationId}:${date}`;
  };

  // Get current assignment for an employee on a date
  const getAssignment = (employeeNumber: string, date: string): string | null => {
    const key = getAssignmentKey(employeeNumber, date);
    if (pendingAssignments.has(key)) {
      return pendingAssignments.get(key) || null;
    }
    const existing = scheduleData?.assignments.find(
      (a) => a.employee_number === employeeNumber && a.assignment_date === date
    );
    return existing?.job_code || null;
  };

  // Check if station is scheduled for a date
  const isStationScheduled = (stationId: number, date: string): boolean => {
    const key = getStationKey(stationId, date);
    if (pendingStations.has(key)) {
      return pendingStations.get(key) || false;
    }
    return scheduleData?.stationSchedules?.some(
      (s) => s.station_id === stationId && s.work_date === date
    ) || false;
  };

  // Get operator's assigned station for a date
  const getOperatorStations = (employeeNumber: string, date: string): number[] => {
    const key = getAssignmentKey(employeeNumber, date);
    if (pendingOperatorStations.has(key)) {
      return pendingOperatorStations.get(key) ?? [];
    }
    return (scheduleData?.operatorStationAssignments || [])
      .filter((a) => a.employee_number === employeeNumber && a.work_date === date)
      .map((a) => a.station_id);
  };

  // Get active stations for a date (stations that are scheduled for that day)
  const getActiveStationsForDate = (date: string): Station[] => {
    if (!scheduleData?.stations) return [];
    return scheduleData.stations.filter((station) => isStationScheduled(station.station_id, date));
  };

  // Parse date string (YYYY-MM-DD) to local Date object
  const parseLocalDate = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const pruneStationAssignmentsForDate = (stationId: number, date: string) => {
    if (!scheduleData?.employees) return;

    setPendingOperatorStations((prev) => {
      const next = new Map(prev);
      let changed = false;

      scheduleData.employees.forEach((employee) => {
        const key = getAssignmentKey(employee.employee_number, date);
        const currentStations = next.has(key)
          ? (next.get(key) ?? [])
          : getOperatorStations(employee.employee_number, date);

        if (!currentStations.includes(stationId)) {
          return;
        }

        next.set(key, currentStations.filter((currentStationId) => currentStationId !== stationId));
        changed = true;
      });

      return changed ? next : prev;
    });
  };

  // Handle assignment change
  const handleAssignmentChange = (employeeNumber: string, date: string, jobCode: string | null) => {
    if (isPastDate(parseLocalDate(date))) return;

    const key = getAssignmentKey(employeeNumber, date);
    const currentAssignment = getAssignment(employeeNumber, date);
    const currentStations = getOperatorStations(employeeNumber, date);

    setPendingAssignments((prev) => {
      const next = new Map(prev);
      next.set(key, jobCode);
      return next;
    });

    if (currentAssignment !== jobCode) {
      let nextStations = currentStations;

      if (!jobCode) {
        nextStations = [];
      } else if (jobCode === '84000') {
        nextStations = currentStations.length > 0 ? [currentStations[0]] : [];
      }

      setPendingOperatorStations((prev) => {
        const next = new Map(prev);
        next.set(key, nextStations);
        return next;
      });
    }

    setHasChanges(true);
  };

  // Handle station toggle
  const handleStationToggle = (stationId: number, date: string) => {
    if (isPastDate(parseLocalDate(date))) return;

    const key = getStationKey(stationId, date);
    const currentlyScheduled = isStationScheduled(stationId, date);
    setPendingStations((prev) => {
      const next = new Map(prev);
      next.set(key, !currentlyScheduled);
      return next;
    });

    if (currentlyScheduled) {
      pruneStationAssignmentsForDate(stationId, date);
    }

    setHasChanges(true);
  };

  // Handle operator station assignment change
  const handleOperatorStationChange = (employeeNumber: string, date: string, stationId: number | null) => {
    if (isPastDate(parseLocalDate(date))) return;

    const key = getAssignmentKey(employeeNumber, date);
    setPendingOperatorStations((prev) => {
      const next = new Map(prev);
      next.set(key, stationId ? [stationId] : []);
      return next;
    });
    setHasChanges(true);
  };

  const handlePackerStationToggle = (employeeNumber: string, date: string, stationId: number) => {
    if (isPastDate(parseLocalDate(date))) return;

    const key = getAssignmentKey(employeeNumber, date);
    const currentStations = getOperatorStations(employeeNumber, date);

    setPendingOperatorStations((prev) => {
      const next = new Map(prev);
      const selectedStations = currentStations.includes(stationId)
        ? currentStations.filter((currentStationId) => currentStationId !== stationId)
        : [...currentStations, stationId].sort((a, b) => a - b);
      next.set(key, selectedStations);
      return next;
    });
    setHasChanges(true);
  };

  // Save all changes
  const saveChanges = async () => {
    if (!hasChanges) return;

    setSaving(true);
    isSavingRef.current = true;
    try {
      // Process assignment changes
      const assignmentsToCreate: Array<{
        employee_number: string;
        job_code: string;
        assignment_date: string;
      }> = [];
      const assignmentsToDelete: Array<{
        employee_number: string;
        assignment_date: string;
        job_code: string;
      }> = [];

      for (const [key, jobCode] of pendingAssignments) {
        const [employeeNumber, date] = key.split(':');
        const existingAssignment = scheduleData?.assignments.find(
          (a) => a.employee_number === employeeNumber && a.assignment_date === date
        );

        if (jobCode) {
          // Create or update assignment
          assignmentsToCreate.push({
            employee_number: employeeNumber,
            job_code: jobCode,
            assignment_date: date,
          });
          // If there was a different assignment, delete it first
          if (existingAssignment && existingAssignment.job_code !== jobCode) {
            assignmentsToDelete.push({
              employee_number: employeeNumber,
              assignment_date: date,
              job_code: existingAssignment.job_code,
            });
          }
        } else if (existingAssignment) {
          // Delete existing assignment
          assignmentsToDelete.push({
            employee_number: employeeNumber,
            assignment_date: date,
            job_code: existingAssignment.job_code,
          });
        }
      }

      // Process station changes - add or remove individually
      const stationsToAdd: Array<{ stationId: number; workDate: string }> = [];
      const stationsToRemove: Array<{ stationId: number; workDate: string }> = [];

      for (const [key, active] of pendingStations) {
        const [stationId, date] = key.split(':');
        if (active) {
          stationsToAdd.push({ stationId: parseInt(stationId), workDate: date });
        } else {
          stationsToRemove.push({ stationId: parseInt(stationId), workDate: date });
        }
      }

      // Execute assignment deletes first
      for (const del of assignmentsToDelete) {
        await workScheduleService.deleteAssignment(del);
      }

      // Then create/update assignments
      if (assignmentsToCreate.length > 0) {
        await workScheduleService.bulkCreateAssignments(assignmentsToCreate);
      }

      // Add station schedules
      for (const { stationId, workDate } of stationsToAdd) {
        await workScheduleService.addStationSchedule(stationId, workDate);
      }

      // Remove station schedules
      for (const { stationId, workDate } of stationsToRemove) {
        await workScheduleService.removeStationScheduleByDetails(stationId, workDate);
      }

      // Process operator station assignments
      for (const [key, stationIds] of pendingOperatorStations) {
        const [employeeNumber, date] = key.split(':');
        await workScheduleService.assignOperatorToStation({
          employee_number: employeeNumber,
          station_ids: stationIds,
          work_date: date,
        });
      }

      toast.success(t('workSchedule.saveSuccess'));
      await loadScheduleData();
    } catch (error) {
      console.error('Failed to save changes:', error);
      toast.error(t('workSchedule.saveError'));
    } finally {
      isSavingRef.current = false;
      setSaving(false);
    }
  };

  // Discard changes
  const discardChanges = () => {
    setPendingAssignments(new Map());
    setPendingStations(new Map());
    setPendingOperatorStations(new Map());
    setHasChanges(false);
  };

  // Copy to next week
  const copyToNextWeek = async () => {
    try {
      setSaving(true);
      const nextWeekStart = toLocalISODate(addDays(currentWeekStart, 7));
      const result = await workScheduleService.copyWeek(
        toLocalISODate(currentWeekStart),
        nextWeekStart
      );
      const totalCopied =
        result.copiedAssignments +
        result.copiedStationSchedules +
        result.copiedOperatorStationAssignments;
      toast.success(t('workSchedule.copySuccess').replace('{{count}}', String(totalCopied)));
    } catch (error) {
      console.error('Failed to copy week:', error);
      toast.error(t('workSchedule.copyError'));
    } finally {
      setSaving(false);
    }
  };

  // Print work schedule - opens new window with formatted HTML table
  const handlePrint = () => {
    if (!scheduleData) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    // Build employee rows HTML
    const employeeRows = scheduleData.employees.map((emp) => {
      const cells = weekDays.map((day) => {
        const dateStr = toLocalISODate(day);
        const assignment = getAssignment(emp.employee_number, dateStr);
        const jobType = jobTypes.find((j) => j.job_code === assignment);
        const jobLabel = jobType?.short_description || '-';
        const operatorStations = getOperatorStations(emp.employee_number, dateStr);
        const stationNames = operatorStations
          .map((stationId) => scheduleData.stations.find((s) => s.station_id === stationId)?.station_name)
          .filter((stationName): stationName is string => Boolean(stationName));

        const bgColor = assignment
          ? assignment === '85000'
            ? 'background-color: #dbeafe;' // blue-100
            : 'background-color: #dcfce7;' // green-100
          : '';

        return `<td style="border: 1px solid #ddd; padding: 8px; text-align: center; ${bgColor}">
          ${jobLabel}${stationNames.length > 0 ? `<br><small style="color: #6366f1;">${stationNames.join(', ')}</small>` : ''}
        </td>`;
      }).join('');

      return `<tr>
        <td style="border: 1px solid #ddd; padding: 8px; white-space: nowrap;">
          <strong>${emp.full_name}</strong><br>
          <small style="color: #666;">${emp.employee_number}</small>
        </td>
        ${cells}
      </tr>`;
    }).join('');

    // Build station rows HTML
    const stationRows = scheduleData.stations.map((station) => {
      const cells = weekDays.map((day) => {
        const dateStr = toLocalISODate(day);
        const scheduled = isStationScheduled(station.station_id, dateStr);
        const bgColor = scheduled ? 'background-color: #f3e8ff;' : ''; // purple-100
        return `<td style="border: 1px solid #ddd; padding: 8px; text-align: center; ${bgColor}">
          ${scheduled ? '✓' : '-'}
        </td>`;
      }).join('');

      return `<tr>
        <td style="border: 1px solid #ddd; padding: 8px; white-space: nowrap;">
          <strong>${station.station_name}</strong>
        </td>
        ${cells}
      </tr>`;
    }).join('');

    // Build header row with day names and dates
    const headerCells = weekDays.map((day) => {
      const { day: dayName, date } = formatDayHeader(day);
      const dateStr = toLocalISODate(day);
      const summary = dailySummaries[dateStr] || { operators: 0, packers: 0, stations: 0 };
      const isWeekend = day.getDay() === 0 || day.getDay() === 6;
      const bgColor = isWeekend ? 'background-color: #f3f4f6;' : '';

      return `<th style="border: 1px solid #d1d5db; padding: 10px 8px; text-align: center; ${bgColor}">
        <div style="font-weight: 600;">${dayName}</div>
        <div style="font-size: 12px; color: #666;">${date}</div>
        <div style="font-size: 10px; margin-top: 4px;">
          <span style="color: #16a34a;">${summary.operators}O</span>
          <span style="color: #2563eb;">${summary.packers}P</span>
          <span style="color: #9333ea;">${summary.stations}S</span>
        </div>
      </th>`;
    }).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${t('workSchedule.weeklySchedule')} - ${getWeekHeader()}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { font-size: 18px; margin-bottom: 10px; }
          .subtitle { font-size: 14px; color: #666; margin-bottom: 15px; }
          table { border-collapse: collapse; width: 100%; font-size: 11px; }
          th { background-color: #e5e7eb; color: #111; padding: 10px 8px; text-align: left; border: 1px solid #d1d5db; font-weight: 600; }
          td { border: 1px solid #ddd; padding: 8px; }
          tr:nth-child(even) { background-color: #f9fafb; }
          .section-header { background-color: #f3f4f6; font-weight: 600; }
          .legend { margin-top: 15px; font-size: 11px; display: flex; gap: 20px; }
          .legend-item { display: flex; align-items: center; gap: 5px; }
          .legend-box { width: 16px; height: 16px; border: 1px solid #ccc; }
          @media print {
            body { margin: 0; padding: 10px; }
            h1 { font-size: 14px; }
          }
        </style>
      </head>
      <body>
        <h1>${t('workSchedule.weeklySchedule')}</h1>
        <div class="subtitle">${getWeekHeader()}</div>
        <table>
          <thead>
            <tr>
              <th style="border: 1px solid #d1d5db; padding: 10px 8px; min-width: 150px;">${t('workSchedule.employee')}</th>
              ${headerCells}
            </tr>
          </thead>
          <tbody>
            ${employeeRows}
            <tr class="section-header">
              <td colspan="8" style="border: 1px solid #ddd; padding: 8px; background-color: #e5e7eb;">
                <strong>${t('workSchedule.activeStations')}</strong>
              </td>
            </tr>
            ${stationRows}
          </tbody>
        </table>
        <div class="legend">
          <div class="legend-item">
            <div class="legend-box" style="background-color: #dcfce7;"></div>
            <span>${t('workSchedule.cutRole')}</span>
          </div>
          <div class="legend-item">
            <div class="legend-box" style="background-color: #dbeafe;"></div>
            <span>${t('workSchedule.packRole')}</span>
          </div>
          <div class="legend-item">
            <div class="legend-box" style="background-color: #f3e8ff;"></div>
            <span>${t('workSchedule.stationActive')}</span>
          </div>
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // Export work schedule to Excel
  const handleExport = () => {
    if (!scheduleData) return;

    // Build header row with day columns
    const headers = [
      t('workSchedule.employee'),
      ...weekDays.map((day) => {
        const { day: dayName, date } = formatDayHeader(day);
        return `${dayName} ${date}`;
      })
    ];

    // Build employee rows
    const employeeRows = scheduleData.employees.map((emp) => {
      const cells = weekDays.map((day) => {
        const dateStr = toLocalISODate(day);
        const assignment = getAssignment(emp.employee_number, dateStr);
        const jobType = jobTypes.find((j) => j.job_code === assignment);
        const jobLabel = jobType?.short_description || '';
        const stationNames = getOperatorStations(emp.employee_number, dateStr)
          .map((stationId) => scheduleData.stations.find((s) => s.station_id === stationId)?.station_name)
          .filter((stationName): stationName is string => Boolean(stationName));
        return stationNames.length > 0 ? `${jobLabel} (${stationNames.join(', ')})` : jobLabel;
      });
      return [`${emp.full_name} (${emp.employee_number})`, ...cells];
    });

    // Build station rows
    const stationRows = scheduleData.stations.map((station) => {
      const cells = weekDays.map((day) => {
        const dateStr = toLocalISODate(day);
        return isStationScheduled(station.station_id, dateStr) ? '✓' : '';
      });
      return [station.station_name, ...cells];
    });

    // Build summary row
    const summaryRow = [
      t('workSchedule.summary'),
      ...weekDays.map((day) => {
        const dateStr = toLocalISODate(day);
        const summary = dailySummaries[dateStr] || { operators: 0, packers: 0, stations: 0 };
        return `${summary.operators}O / ${summary.packers}P / ${summary.stations}S`;
      })
    ];

    // Create worksheet data with empty row before Active Stations section
    const wsData = [
      headers,
      ...employeeRows,
      [], // Empty row as visual separator
      [t('workSchedule.activeStations'), '', '', '', '', '', '', ''], // Section header
      ...stationRows,
      [], // Empty row
      summaryRow
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Header style with gray background and borders
    const headerStyle = {
      fill: { fgColor: { rgb: 'D1D5DB' } }, // gray-300 for more visible header
      font: { bold: true },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: '9CA3AF' } },
        bottom: { style: 'thin', color: { rgb: '9CA3AF' } },
        left: { style: 'thin', color: { rgb: '9CA3AF' } },
        right: { style: 'thin', color: { rgb: '9CA3AF' } }
      }
    };

    // Section header style (Active Stations row)
    const sectionHeaderStyle = {
      fill: { fgColor: { rgb: 'E5E7EB' } }, // gray-200
      font: { bold: true },
      border: {
        top: { style: 'medium', color: { rgb: '6B7280' } }, // Thicker top border as separator
        bottom: { style: 'thin', color: { rgb: '9CA3AF' } },
        left: { style: 'thin', color: { rgb: '9CA3AF' } },
        right: { style: 'thin', color: { rgb: '9CA3AF' } }
      }
    };

    // Apply header style to first row
    headers.forEach((_, colIndex) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIndex });
      if (ws[cellRef]) {
        ws[cellRef].s = headerStyle;
      }
    });

    // Calculate row index for "Active Stations" section header
    // Row 0 = header, then employee rows, then empty row, then Active Stations
    const activeStationsRowIndex = 1 + employeeRows.length + 1; // +1 for header, +1 for empty row

    // Apply section header style to "Active Stations" row
    headers.forEach((_, colIndex) => {
      const cellRef = XLSX.utils.encode_cell({ r: activeStationsRowIndex, c: colIndex });
      if (!ws[cellRef]) {
        ws[cellRef] = { v: '', t: 's' };
      }
      ws[cellRef].s = sectionHeaderStyle;
    });

    // Set column widths
    ws['!cols'] = [
      { wch: 30 }, // Employee name column
      ...weekDays.map(() => ({ wch: 15 })) // Day columns
    ];

    // Create workbook and add worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Work Schedule');

    // Download file
    const dateStr = toLocalISODate(currentWeekStart);
    XLSX.writeFile(wb, `work-schedule-${dateStr}.xlsx`);
  };

  // Calculate daily summaries with pending changes
  const dailySummaries = useMemo(() => {
    const summaries: Record<string, { operators: number; packers: number; stations: number }> = {};

    weekDays.forEach((day) => {
      const dateStr = toLocalISODate(day);
      let operators = 0;
      let packers = 0;
      let stations = 0;

      // Count employees
      scheduleData?.employees.forEach((emp) => {
        const assignment = getAssignment(emp.employee_number, dateStr);
        if (assignment === '85000') {
          packers++;
        } else if (assignment) {
          operators++;
        }
      });

      // Count stations
      scheduleData?.stations.forEach((station) => {
        if (isStationScheduled(station.station_id, dateStr)) {
          stations++;
        }
      });

      summaries[dateStr] = { operators, packers, stations };
    });

    return summaries;
  }, [weekDays, scheduleData, pendingAssignments, pendingStations]);

  if (loading && !scheduleData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with navigation — matches Daily Queue layout */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Mobile: compact nav */}
        <div className="sm:hidden flex items-center justify-between px-3 py-2">
          <button
            onClick={goToCurrentWeek}
            className="px-2 py-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors border border-indigo-300 dark:border-indigo-600"
          >
            {t('planning.today')}
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateWeek(-1)}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <span className="text-sm font-semibold text-gray-900 dark:text-white text-center">
              {getWeekHeaderShort()}
            </span>
            <button
              onClick={() => navigateWeek(1)}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
          <div className="w-8" />
        </div>

        {/* Desktop: full nav bar */}
        <div className="hidden sm:grid grid-cols-3 items-center gap-2 px-4 py-3">
          {/* Left: Today button */}
          <div className="flex items-center gap-2 justify-self-start">
            <button
              onClick={goToCurrentWeek}
              className="px-3 py-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors border border-indigo-300 dark:border-indigo-600"
            >
              {t('planning.today')}
            </button>
          </div>

          {/* Center: Week navigation */}
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => navigateWeek(-1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <span className="text-lg font-semibold text-gray-900 dark:text-white min-w-[220px] text-center">
              {getWeekHeader()}
            </span>
            <button
              onClick={() => navigateWeek(1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2 justify-self-end">
            <button
              onClick={handlePrint}
              disabled={!scheduleData}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50"
              title={t('common.print')}
            >
              <Printer className="w-4 h-4" />
            </button>
            <button
              onClick={handleExport}
              disabled={!scheduleData}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
              title={t('common.export')}
            >
              <Download className="w-4 h-4" />
            </button>
            {!isReadOnly && (
              <button
                onClick={copyToNextWeek}
                disabled={saving}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50"
              >
                <Copy className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Changes bar (hidden for read-only users) */}
      {hasChanges && !isReadOnly && (
        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg p-3 flex items-center justify-between">
          <span className="text-amber-800 dark:text-amber-200 text-sm">
            {t('workSchedule.unsavedChanges')}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={discardChanges}
              disabled={saving}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-amber-100 dark:hover:bg-amber-800/50 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
              {t('common.cancel')}
            </button>
            <button
              onClick={saveChanges}
              disabled={saving}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <Check className="w-4 h-4" />
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </div>
      )}

      {/* Schedule grid */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            {/* Header row with days */}
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="sticky left-0 bg-gray-50 dark:bg-gray-900 px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300 w-48 z-10">
                  {t('workSchedule.employee')}
                </th>
                {weekDays.map((day) => {
                  const { day: dayName, date } = formatDayHeader(day);
                  const dateStr = toLocalISODate(day);
                  const summary = dailySummaries[dateStr] || { operators: 0, packers: 0, stations: 0 };
                  const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                  const isPast = isPastDate(day);
                  const isToday = toLocalISODate(new Date()) === dateStr;

                  return (
                    <th
                      key={dateStr}
                      className={`px-2 py-2 text-center min-w-[100px] ${
                        isWeekend ? 'bg-gray-100 dark:bg-gray-900/50' : 'bg-gray-50 dark:bg-gray-900'
                      } ${isPast ? 'opacity-60' : ''} ${isToday ? 'ring-2 ring-inset ring-indigo-500' : ''}`}
                    >
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {dayName}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{date}</div>
                      {/* Daily summary */}
                      <div className="flex items-center justify-center gap-1 mt-1 text-[10px]">
                        <span className="text-green-600 dark:text-green-400" title={t('workSchedule.operators')}>
                          {summary.operators}O
                        </span>
                        <span className="text-blue-600 dark:text-blue-400" title={t('workSchedule.packers')}>
                          {summary.packers}P
                        </span>
                        <span className="text-purple-600 dark:text-purple-400" title={t('workSchedule.stations')}>
                          {summary.stations}S
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {/* Employee rows */}
              {scheduleData?.employees.map((employee) => (
                <tr key={employee.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="sticky left-0 bg-white dark:bg-gray-800 px-4 py-2 z-10">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {employee.full_name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {employee.employee_number}
                        </div>
                      </div>
                    </div>
                  </td>
                  {weekDays.map((day) => {
                    const dateStr = toLocalISODate(day);
                    const assignment = getAssignment(employee.employee_number, dateStr);
                    const operatorStations = getOperatorStations(employee.employee_number, dateStr);
                    const activeStations = getActiveStationsForDate(dateStr);
                    const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                    const isPast = isPastDate(day);

                    return (
                      <td
                        key={dateStr}
                        className={`px-2 py-2 text-center ${
                          isWeekend ? 'bg-gray-50 dark:bg-gray-900/30' : ''
                        } ${isPast ? 'opacity-60' : ''}`}
                      >
                        <div className="flex flex-col gap-1">
                          <select
                            value={assignment || ''}
                            onChange={(e) =>
                              handleAssignmentChange(
                                employee.employee_number,
                                dateStr,
                                e.target.value || null
                              )
                            }
                            disabled={isPast || isReadOnly}
                            className={`w-full text-xs px-2 py-1 rounded border ${
                              assignment
                                ? assignment === '85000'
                                  ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200'
                                  : 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700 text-green-800 dark:text-green-200'
                                : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400'
                            } disabled:cursor-not-allowed`}
                          >
                            <option value="">{t('workSchedule.off')}</option>
                            {jobTypes.map((job) => (
                              <option key={job.job_code} value={job.job_code}>
                                {job.short_description}
                              </option>
                            ))}
                          </select>
                          {/* Station dropdown - only show when job is assigned */}
                          {assignment && activeStations.length > 0 && (
                            assignment === '85000' ? (
                              <div className="flex flex-wrap gap-1">
                                {activeStations.map((station) => {
                                  const selected = operatorStations.includes(station.station_id);
                                  return (
                                    <button
                                      key={station.station_id}
                                      type="button"
                                      onClick={() =>
                                        handlePackerStationToggle(
                                          employee.employee_number,
                                          dateStr,
                                          station.station_id
                                        )
                                      }
                                      disabled={isPast || isReadOnly}
                                      className={`px-2 py-1 rounded-full border text-[11px] transition-colors disabled:cursor-not-allowed ${
                                        selected
                                          ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200'
                                          : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                                      }`}
                                    >
                                      {station.station_name}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : (
                              <select
                                value={operatorStations[0] || ''}
                                onChange={(e) =>
                                  handleOperatorStationChange(
                                    employee.employee_number,
                                    dateStr,
                                    e.target.value ? parseInt(e.target.value) : null
                                  )
                                }
                                disabled={isPast || isReadOnly}
                                className="w-full text-xs px-1 py-0.5 rounded border bg-indigo-50 dark:bg-indigo-900/30 border-indigo-200 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 disabled:cursor-not-allowed"
                              >
                                <option value="">--</option>
                                {activeStations.map((station) => (
                                  <option key={station.station_id} value={station.station_id}>
                                    {station.station_name}
                                  </option>
                                ))}
                              </select>
                            )
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}

              {/* Separator row - first cell sticky like Employee header */}
              <tr className="bg-gray-100 dark:bg-gray-900">
                <td className="sticky left-0 bg-gray-100 dark:bg-gray-900 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 z-10">
                  <div className="flex items-center gap-2">
                    <Factory className="w-4 h-4" />
                    {t('workSchedule.activeStations')}
                  </div>
                </td>
                {weekDays.map((day) => (
                  <td key={toLocalISODate(day)} className="bg-gray-100 dark:bg-gray-900"></td>
                ))}
              </tr>

              {/* Station rows */}
              {scheduleData?.stations.map((station) => (
                <tr key={station.station_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="sticky left-0 bg-white dark:bg-gray-800 px-4 py-2 z-10">
                    <div className="flex items-center gap-2">
                      <Factory className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {station.station_name}
                      </span>
                    </div>
                  </td>
                  {weekDays.map((day) => {
                    const dateStr = toLocalISODate(day);
                    const scheduled = isStationScheduled(station.station_id, dateStr);
                    const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                    const isPast = isPastDate(day);

                    return (
                      <td
                        key={dateStr}
                        className={`px-2 py-2 text-center ${
                          isWeekend ? 'bg-gray-50 dark:bg-gray-900/30' : ''
                        } ${isPast ? 'opacity-60' : ''}`}
                      >
                        <button
                          onClick={() => !isReadOnly && handleStationToggle(station.station_id, dateStr)}
                          disabled={isPast || isReadOnly}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                            scheduled
                              ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border-2 border-purple-400 dark:border-purple-600'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border border-gray-300 dark:border-gray-600'
                          } disabled:cursor-not-allowed mx-auto`}
                        >
                          {scheduled && <Check className="w-4 h-4" />}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow px-4 py-3">
        <div className="flex flex-wrap items-center gap-4 text-xs">
          <span className="text-gray-500 dark:text-gray-400">{t('planning.legend')}:</span>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700"></div>
            <span className="text-gray-600 dark:text-gray-300">{t('workSchedule.cutRole')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700"></div>
            <span className="text-gray-600 dark:text-gray-300">{t('workSchedule.packRole')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-purple-100 dark:bg-purple-900/30 border-2 border-purple-400 dark:border-purple-600"></div>
            <span className="text-gray-600 dark:text-gray-300">{t('workSchedule.stationActive')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 rounded bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600"></div>
            <span className="text-gray-600 dark:text-gray-300">{t('workSchedule.off')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkScheduleContent;
